import { useEffect, useState } from "react";

/**
 * Milliseconds since `startedAt`, ticking twice a second.
 *
 * Accepts an ISO string or epoch milliseconds so the clock survives a reload
 * on work that outlives the mount, and falls back to mount time when it is
 * given nothing.
 *
 * Lifted out of `StartupScreen` when the auto-annotate run panel needed the
 * same thing: a labeller waiting 20-190 s on a Robopipe Vision turn has no
 * other way to tell a fast run from a stalled one.
 */
export const useElapsed = (startedAt?: string | number | null): number => {
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const parsed =
    typeof startedAt === "string"
      ? Date.parse(startedAt)
      : typeof startedAt === "number"
        ? startedAt
        : NaN;
  const start = Number.isFinite(parsed) ? parsed : mountedAt;
  // Clamped: a client clock running behind the server's would otherwise give a
  // negative elapsed time and reset the step progression to the first step.
  return Math.max(0, now - start);
};

/** `0:07`, `1:47`, `12:03` — a wall clock, not a duration sentence. */
export const formatElapsed = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};
